import { GraphItem } from '@ardoq/graph';
import {
  type IEdge,
  type INode,
  IncrementalEdgeHint,
  IncrementalNodeHint,
} from '@ardoq/yfiles';

type TagOwner = { tag: unknown };

type EdgeEndpoints = { source: string | null; target: string | null };

type ResolvedEdgeEndpoints = { source: string; target: string };

export const incrementalNodeHint =
  (newIds: Set<string>) =>
  ({ tag }: TagOwner): IncrementalNodeHint => {
    if (!(tag instanceof GraphItem) || !newIds.has(tag.id)) {
      return IncrementalNodeHint.KEEP_RELATIVE_ORDER;
    }
    if (tag.isGroup()) {
      return IncrementalNodeHint.INCREMENTAL_GROUP;
    }
    if (tag.isComponent()) {
      return IncrementalNodeHint.INCREMENTAL;
    }
    return IncrementalNodeHint.INCREMENTAL_WITH_LAYERS_FROM_SKETCH;
  };

export const incrementalEdgeHint =
  (newIds: Set<string>) =>
  ({ tag }: TagOwner): IncrementalEdgeHint =>
    tag instanceof GraphItem && newIds.has(tag.id)
      ? IncrementalEdgeHint.INCREMENTAL
      : IncrementalEdgeHint.KEEP_RELATIVE_ORDER;

const tagId = (node: INode | null): string | null =>
  node?.tag instanceof GraphItem ? node.tag.id : null;

export const toEdgeEndpoints = ({
  sourceNode,
  targetNode,
}: IEdge): EdgeEndpoints => ({
  source: tagId(sourceNode),
  target: tagId(targetNode),
});

const isEdgeBetweenDistinctNewIds =
  (newIds: Set<string>) =>
  (edge: EdgeEndpoints): edge is ResolvedEdgeEndpoints =>
    edge.source !== null &&
    edge.target !== null &&
    edge.source !== edge.target &&
    newIds.has(edge.source) &&
    newIds.has(edge.target);

export const getNewIdsTargetedByNewIds = (
  newIds: Set<string>,
  edgeEndpoints: EdgeEndpoints[]
): Set<string> =>
  new Set(
    edgeEndpoints
      .filter(isEdgeBetweenDistinctNewIds(newIds))
      .map(({ target }) => target)
  );
